import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";

interface JournalEntry {
  id: number;
  title: string;
  content: string;
  mood_rating: string;
  mood_tags: string[];
  created_at: string;
  ai_verse: string | null;
  ai_prayer: string | null;
}

interface UserJournalDialogProps {
  userId: string;
  userName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const UserJournalDialog = ({
  userId,
  userName,
  open,
  onOpenChange,
}: UserJournalDialogProps) => {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open && userId) {
      fetchJournalEntries();
    }
  }, [open, userId]);

  const fetchJournalEntries = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("journal_entries")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setEntries(data || []);
    } catch (error) {
      console.error("Error fetching journal entries:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getMoodColor = (mood: string) => {
    const colors: Record<string, string> = {
      excellent: "bg-green-500",
      good: "bg-blue-500",
      neutral: "bg-yellow-500",
      down: "bg-orange-500",
      struggling: "bg-red-500",
    };
    return colors[mood] || "bg-gray-500";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] bg-app-surface border-app-border">
        <DialogHeader>
          <DialogTitle className="text-app-text-primary">
            Journal Entries - {userName}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : entries.length === 0 ? (
          <div className="py-12 text-center text-app-text-secondary">
            No journal entries found
          </div>
        ) : (
          <ScrollArea className="h-[60vh] pr-4">
            <div className="space-y-4">
              {entries.map((entry) => (
                <div
                  key={entry.id}
                  className="p-4 rounded-lg border border-app-border bg-app-surface/50 space-y-3"
                >
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-semibold text-app-text-primary">
                      {entry.title}
                    </h3>
                    <div className="flex items-center gap-2">
                      <Badge
                        className={`${getMoodColor(entry.mood_rating)} text-white`}
                      >
                        {entry.mood_rating}
                      </Badge>
                      <span className="text-xs text-app-text-secondary whitespace-nowrap">
                        {new Date(entry.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <p className="text-app-text-secondary whitespace-pre-wrap">
                    {entry.content}
                  </p>

                  {entry.mood_tags && entry.mood_tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {entry.mood_tags.map((tag, idx) => (
                        <Badge
                          key={idx}
                          variant="secondary"
                          className="text-xs"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {entry.ai_verse && (
                    <div className="mt-3 p-3 rounded bg-primary/10 border border-primary/20">
                      <p className="text-sm font-medium text-primary mb-1">
                        AI Verse Suggestion:
                      </p>
                      <p className="text-sm text-app-text-secondary italic">
                        {entry.ai_verse}
                      </p>
                    </div>
                  )}

                  {entry.ai_prayer && (
                    <div className="mt-3 p-3 rounded bg-primary/10 border border-primary/20">
                      <p className="text-sm font-medium text-primary mb-1">
                        AI Prayer:
                      </p>
                      <p className="text-sm text-app-text-secondary">
                        {entry.ai_prayer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};
